import { currentPageKey, resolveField, resolveSection } from '/bb/identifier.js';
import bbCategoryContract from '../../../constants/bb-category-contract.json';

const OPEN_STORAGE_KEY = 'rs_bb_devtools_open';
const PANEL_POSITION_STORAGE_KEY = 'rs_bb_devtools_panel_position';
const LIVE_REFRESH_MS = 1000;
const PANEL_POSITIONS = ['right', 'bottom', 'full'];

const TABS = [
  { id: 'overview', label: 'Status' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'fields', label: 'Fields' },
  { id: 'sections', label: 'Sections' },
  { id: 'saves', label: 'Saves' },
  { id: 'scores', label: 'Scores' },
  { id: 'raw', label: 'Raw' },
  { id: 'config', label: 'Config' },
];

const SCORE_KEYS = ['cognitive_load', 'hesitation', 'digital_fluency', 'impulsivity', 'research_intent'];
const FIELD_FILTER_KEYS = ['personal', 'amount', 'sensitive', 'unrecognized'];
const BB_ATTRIBUTES_MODE = {
  MERGE: 'merge',
  REPLACE: 'replace',
};

const FIELD_CATEGORIES = bbCategoryContract.field_categories;
const SECTION_CATEGORIES = bbCategoryContract.section_categories;

let metricSearch = '';
let fieldCategoryFilters = {};
let scoreFilters = {};
let metricsPathsCollapsed = false;
let metricGroupsCollapsed = {};
let fieldDetailsOpen = {};
let sectionDetailsOpen = {};
let savePayloadDetailsOpen = {};
let scorePanelsCollapsed = {};
let scrollableState = {};
let inspectSelection = null;
let panelPosition = readPanelPosition();

export function createBbDevToolsUi({ bridge }) {
  let host = null;
  let root = null;
  let activeTab = 'overview';
  let timer = null;
  let isOpen = readOpenState();
  let latestData = null;
  let inspectMode = false;
  let inspectSuppressClickUntil = 0;
  let pendingFocus = null;
  let inspectOverlay = null;

  return {
    mount,
    open,
    close,
    toggle,
  };

  function mount() {
    if (host || typeof document === 'undefined') return;

    host = document.createElement('div');
    host.setAttribute('data-rs-bb-devtools-host', 'true');
    root = host.attachShadow({ mode: 'open' });
    root.innerHTML = renderShell();
    document.documentElement.appendChild(host);

    root.addEventListener('click', handleClick);
    root.addEventListener('input', handleInput);
    root.addEventListener('change', handleChange);
    root.addEventListener('toggle', handleToggle, true);
    render();

    if (isOpen) startLiveUpdates();
  }

  function open() {
    isOpen = true;
    writeOpenState(true);
    mount();
    startLiveUpdates();
    render();
  }

  function close() {
    isOpen = false;
    writeOpenState(false);
    stopLiveUpdates();
    render();
  }

  function toggle() {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }

  function startLiveUpdates() {
    if (timer !== null) return;

    refresh();
    timer = setInterval(refresh, LIVE_REFRESH_MS);
  }

  function stopLiveUpdates() {
    if (timer === null) return;

    clearInterval(timer);
    timer = null;
  }

  function refresh() {
    latestData = bridge.read();
    if (shouldDeferLiveRender()) return;

    render();
  }

  function shouldDeferLiveRender() {
    if (!root || !isOpen) return false;

    const active = root.activeElement;
    if (!active || !isInteractiveControl(active)) return false;

    return true;
  }

  function isInteractiveControl(element) {
    const tagName = element && element.tagName ? element.tagName.toLowerCase() : '';
    return tagName === 'select' || tagName === 'input' || tagName === 'textarea';
  }

  function render() {
    if (!root) return;

    if (!latestData) latestData = bridge.read();

    const button = root.querySelector('.rs-bb-devtools-button');
    const panel = root.querySelector('.rs-bb-devtools-panel');
    const body = root.querySelector('.rs-bb-devtools-body');
    const header = root.querySelector('.rs-bb-devtools-header');
    const tabs = root.querySelector('.rs-bb-devtools-tabs');

    if (!button || !panel || !body || !header || !tabs) return;

    const debug = latestData.debug || {};
    const lifecycle = debug.lifecycle || 'idle';
    const bodyScrollTop = body.scrollTop;
    const bodyScrollLeft = body.scrollLeft;
    const focusedSearch =
      root.activeElement && root.activeElement.getAttribute('data-rs-bb-devtools-search') === 'true';
    const searchSelectionStart = focusedSearch ? root.activeElement.selectionStart : null;
    const searchSelectionEnd = focusedSearch ? root.activeElement.selectionEnd : null;

    captureFieldDetailsOpenState(body);
    captureSectionDetailsOpenState(body);
    captureSavePayloadDetailsOpenState(body);
    captureScrollableState(body);
    applyPendingInspectOpen();
    const shouldFocusInspectTarget = !!pendingFocus;

    button.className = 'rs-bb-devtools-button status-' + escapeClass(lifecycle);
    button.textContent = 'BB';
    panel.className = 'rs-bb-devtools-panel position-' + escapeClass(panelPosition);
    panel.style.display = isOpen ? 'flex' : 'none';
    header.innerHTML = renderHeader(latestData, inspectMode);
    if (tabs.getAttribute('data-rs-bb-devtools-active-tab') !== activeTab) {
      tabs.innerHTML = renderTabs(activeTab);
      tabs.setAttribute('data-rs-bb-devtools-active-tab', activeTab);
    }
    body.innerHTML = renderActiveTab(latestData);
    if (inspectMode) {
      installInspectListeners();
    } else {
      removeInspectListeners();
    }
    restoreScrollableState(body);
    if (shouldFocusInspectTarget) {
      focusPendingInspectTarget(body);
    } else {
      body.scrollTop = bodyScrollTop;
      body.scrollLeft = bodyScrollLeft;
      restoreBodyScroll(body, bodyScrollTop, bodyScrollLeft);
    }

    if (focusedSearch) {
      const search = root.querySelector('[data-rs-bb-devtools-search="true"]');
      if (search) {
        search.focus();
        if (typeof search.setSelectionRange === 'function') {
          search.setSelectionRange(searchSelectionStart, searchSelectionEnd);
        }
      }
    }
  }

  function handleClick(event) {
    const actionEl = closest(event.target, '[data-rs-bb-devtools-action]');
    if (actionEl) {
      const action = actionEl.getAttribute('data-rs-bb-devtools-action');
      if (action === 'toggle') toggle();
      if (action === 'close') close();
      if (action === 'copy') copyCurrentPayload();
      if (action === 'copy-processed-response') copyProcessedResponse();
      if (action === 'copy-save-payload') {
        copySavePayload(actionEl.getAttribute('data-rs-bb-devtools-save-payload-key'));
      }
      if (action === 'toggle-metric-paths') toggleMetricPaths();
      if (action === 'toggle-metric-group')
        toggleMetricGroup(actionEl.getAttribute('data-rs-bb-devtools-metric-group'));
      if (action === 'toggle-metric-groups-all') toggleAllMetricGroups();
      if (action === 'toggle-score-panel') toggleScorePanel(actionEl.getAttribute('data-rs-bb-devtools-score'));
      if (action === 'toggle-score-panels-all') toggleAllScorePanels();
      if (action === 'toggle-save-payload')
        toggleSavePayload(actionEl.getAttribute('data-rs-bb-devtools-save-payload-key'));
      if (action === 'set-panel-position') setPanelPosition(actionEl.getAttribute('data-rs-bb-devtools-position'));
      if (action === 'toggle-inspect') toggleInspectMode();
      if (action === 'reset-session') resetSession();
      return;
    }

    const tabEl = closest(event.target, '[data-rs-bb-devtools-tab]');
    if (!tabEl) return;

    activeTab = tabEl.getAttribute('data-rs-bb-devtools-tab') || 'overview';
    render();
  }

  function handleInput(event) {
    if (!event.target || event.target.getAttribute('data-rs-bb-devtools-search') !== 'true') return;

    metricSearch = event.target.value || '';
    render();
  }

  function handleChange(event) {
    const target = event.target;
    if (!target) return;

    if (target.getAttribute('data-rs-bb-devtools-score-filter-select') === 'true') {
      setScoreFilter(target.value || '');
      return;
    }

    if (target.getAttribute('data-rs-bb-devtools-field-category-filter-select') === 'true') {
      setFieldCategoryFilter(target.value || '');
    }
  }

  function handleToggle(event) {
    const target = event.target;
    if (!target || target.nodeName !== 'DETAILS') return;

    const savePayloadKey = target.getAttribute('data-rs-bb-devtools-save-payload-key');
    if (savePayloadKey) {
      savePayloadDetailsOpen[savePayloadKey] = target.open === true;
      return;
    }

    const fieldKey = target.getAttribute('data-rs-bb-devtools-field-key');
    if (fieldKey) {
      fieldDetailsOpen[fieldKey] = target.open === true;
      return;
    }

    const sectionKey = target.getAttribute('data-rs-bb-devtools-section-key');
    if (sectionKey) {
      sectionDetailsOpen[sectionKey] = target.open === true;
    }
  }

  function copyCurrentPayload() {
    const payload = activeTab === 'saves' ? latestData.saves : latestData.metrics;
    const text = JSON.stringify(payload || {}, null, 2);

    copyText(text);
  }

  function copyProcessedResponse() {
    const processed = latestData && latestData.latest_processed ? latestData.latest_processed.data : null;
    const text = JSON.stringify(processed || {}, null, 2);

    copyText(text);
  }

  function copySavePayload(payloadKey) {
    const saves = latestData && Array.isArray(latestData.saves) ? latestData.saves : [];
    const save = saves.find(function (item) {
      return savePayloadDetailsKey(item) === payloadKey;
    });
    const text = JSON.stringify((save && save.sent_payload) || {}, null, 2);

    copyText(text);
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(function () {});
    }
  }

  function toggleMetricPaths() {
    metricsPathsCollapsed = !metricsPathsCollapsed;
    render();
  }

  function toggleMetricGroup(category) {
    if (!category) return;

    metricGroupsCollapsed[category] = !isMetricGroupCollapsed(category);
    render();
  }

  function toggleAllMetricGroups() {
    const categories = currentMetricCategories();
    const nextCollapsed = !areAllMetricGroupsCollapsed(categories);

    for (let i = 0; i < categories.length; i += 1) {
      metricGroupsCollapsed[categories[i]] = nextCollapsed;
    }
    render();
  }

  function currentMetricCategories() {
    const metrics = latestData && latestData.metrics ? latestData.metrics : {};
    const rows = [];
    const scoreContext = createScoreLabelContext(metrics);
    flattenMetrics(metrics, '', rows);
    return Object.keys(groupByCategory(filterRows(rows, scoreContext)));
  }

  function setScoreFilter(score) {
    scoreFilters = {};
    if (SCORE_KEYS.indexOf(score) !== -1) scoreFilters[score] = true;
    render();
  }

  function setFieldCategoryFilter(category) {
    fieldCategoryFilters = {};
    if (FIELD_FILTER_KEYS.indexOf(category) !== -1) fieldCategoryFilters[category] = true;
    render();
  }

  function setPanelPosition(position) {
    if (PANEL_POSITIONS.indexOf(position) === -1) return;

    panelPosition = position;
    writePanelPosition(position);
    render();
  }

  function toggleInspectMode() {
    if (inspectMode) {
      stopInspectMode();
    } else {
      startInspectMode();
    }
  }

  function resetSession() {
    Promise.resolve(bridge.resetSession()).then(
      function (data) {
        latestData = data || bridge.read();
        metricSearch = '';
        inspectSelection = null;
        fieldDetailsOpen = {};
        sectionDetailsOpen = {};
        savePayloadDetailsOpen = {};
        scrollableState = {};
        render();
      },
      function () {
        latestData = bridge.read();
        render();
      }
    );
  }

  function startInspectMode() {
    inspectMode = true;
    isOpen = true;
    writeOpenState(true);
    startLiveUpdates();
    installInspectListeners();
    render();
  }

  function stopInspectMode() {
    inspectMode = false;
    removeInspectListeners();
    hideInspectOverlay();
    render();
  }

  function toggleScorePanel(score) {
    if (!score) return;

    scorePanelsCollapsed[score] = !isScorePanelCollapsed(score);
    render();
  }

  function toggleAllScorePanels() {
    const nextCollapsed = !areAllScorePanelsCollapsed();

    for (let i = 0; i < SCORE_KEYS.length; i += 1) {
      scorePanelsCollapsed[SCORE_KEYS[i]] = nextCollapsed;
    }
    render();
  }

  function toggleSavePayload(payloadKey) {
    if (!payloadKey) return;

    savePayloadDetailsOpen[payloadKey] = !savePayloadDetailsOpen[payloadKey];
    render();
  }

  function captureFieldDetailsOpenState(container) {
    const details = container.querySelectorAll('[data-rs-bb-devtools-field-key]');

    for (let i = 0; i < details.length; i += 1) {
      const key = details[i].getAttribute('data-rs-bb-devtools-field-key');
      if (!key) continue;
      fieldDetailsOpen[key] = details[i].open === true;
    }
  }

  function captureSectionDetailsOpenState(container) {
    const details = container.querySelectorAll('[data-rs-bb-devtools-section-key]');

    for (let i = 0; i < details.length; i += 1) {
      const key = details[i].getAttribute('data-rs-bb-devtools-section-key');
      if (!key) continue;
      sectionDetailsOpen[key] = details[i].open === true;
    }
  }

  function captureSavePayloadDetailsOpenState(container) {
    const details = container.querySelectorAll('details[data-rs-bb-devtools-save-payload-key]');

    for (let i = 0; i < details.length; i += 1) {
      const key = details[i].getAttribute('data-rs-bb-devtools-save-payload-key');
      if (!key) continue;
      savePayloadDetailsOpen[key] = details[i].open === true;
    }
  }

  function captureScrollableState(container) {
    const nodes = container.querySelectorAll('[data-rs-bb-devtools-scroll-key]');

    for (let i = 0; i < nodes.length; i += 1) {
      const key = nodes[i].getAttribute('data-rs-bb-devtools-scroll-key');
      if (!key) continue;
      scrollableState[key] = {
        left: nodes[i].scrollLeft,
        top: nodes[i].scrollTop,
      };
    }
  }

  function restoreScrollableState(container) {
    const nodes = container.querySelectorAll('[data-rs-bb-devtools-scroll-key]');

    for (let i = 0; i < nodes.length; i += 1) {
      const key = nodes[i].getAttribute('data-rs-bb-devtools-scroll-key');
      const state = key ? scrollableState[key] : null;
      if (!state) continue;
      nodes[i].scrollTop = state.top;
      nodes[i].scrollLeft = state.left;
    }
  }

  function restoreBodyScroll(body, scrollTop, scrollLeft) {
    if (typeof requestAnimationFrame !== 'function') return;

    requestAnimationFrame(function () {
      body.scrollTop = scrollTop;
      body.scrollLeft = scrollLeft;
      restoreScrollableState(body);
      focusPendingInspectTarget(body);
    });
  }

  function installInspectListeners() {
    if (installInspectListeners.installed || typeof document === 'undefined') return;

    document.addEventListener('pointermove', handleInspectPointerMove, true);
    document.addEventListener('pointerdown', handleInspectPointerDown, true);
    document.addEventListener('click', handleInspectClick, true);
    document.addEventListener('keydown', handleInspectKeydown, true);
    installInspectListeners.installed = true;
  }

  function removeInspectListeners() {
    if (!installInspectListeners.installed || typeof document === 'undefined') return;

    document.removeEventListener('pointermove', handleInspectPointerMove, true);
    document.removeEventListener('pointerdown', handleInspectPointerDown, true);
    document.removeEventListener('click', handleInspectClick, true);
    document.removeEventListener('keydown', handleInspectKeydown, true);
    installInspectListeners.installed = false;
  }

  function handleInspectPointerMove(event) {
    if (!inspectMode || isDevToolsEvent(event)) return;

    const target = resolveInspectTarget(event.target, event.clientX, event.clientY);
    updateInspectOverlay(target);
  }

  function handleInspectClick(event) {
    if (!inspectMode || isDevToolsEvent(event)) return;
    if (Date.now() < inspectSuppressClickUntil) {
      consumeInspectEvent(event);
      return;
    }

    const target = resolveInspectTarget(event.target, event.clientX, event.clientY);
    if (!target) return;

    consumeInspectEvent(event);
    if (inspectMode) selectInspectTarget(target);
  }

  function handleInspectPointerDown(event) {
    if (!inspectMode || isDevToolsEvent(event)) return;

    const target = resolveInspectTarget(event.target, event.clientX, event.clientY);
    if (!target) return;

    consumeInspectEvent(event);
    inspectSuppressClickUntil = Date.now() + 500;
    selectInspectTarget(target);
  }

  function handleInspectKeydown(event) {
    if (!inspectMode || isDevToolsEvent(event)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      stopInspectMode();
    }
  }

  function selectInspectTarget(target) {
    const key = inspectTargetKey(target);
    inspectSelection = { type: target.type, key };
    pendingFocus = { type: target.type, key };

    if (target.type === 'field') {
      activeTab = 'fields';
      fieldDetailsOpen[key] = true;
    } else {
      activeTab = 'sections';
      sectionDetailsOpen[key] = true;
    }

    render();
  }

  function consumeInspectEvent(event) {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
  }

  function focusPendingInspectTarget(body) {
    if (!pendingFocus || !body) return;

    const selector =
      pendingFocus.type === 'field'
        ? '[data-rs-bb-devtools-field-card-key="' + cssEscape(pendingFocus.key) + '"]'
        : '[data-rs-bb-devtools-section-card-key="' + cssEscape(pendingFocus.key) + '"]';
    const card = body.querySelector(selector);

    if (!card) return;

    scrollCardIntoView(body, card);
    if (typeof card.focus === 'function') card.focus({ preventScroll: true });
    pendingFocus = null;
  }

  function scrollCardIntoView(body, card) {
    const bodyRect = body.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const offset = cardRect.top - bodyRect.top;
    const targetTop = body.scrollTop + offset - 8;

    body.scrollTop = Math.max(0, targetTop);
    body.scrollLeft = 0;
  }

  function applyPendingInspectOpen() {
    if (!pendingFocus) return;

    if (pendingFocus.type === 'field') {
      fieldDetailsOpen[pendingFocus.key] = true;
    } else {
      sectionDetailsOpen[pendingFocus.key] = true;
    }
  }

  function isDevToolsEvent(event) {
    if (!host || !event) return false;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.indexOf(host) !== -1) return true;
    return event.target === host || (host.contains && host.contains(event.target));
  }

  function updateInspectOverlay(target) {
    if (!target || !target.element || !target.element.getBoundingClientRect) {
      hideInspectOverlay();
      return;
    }

    const rect = target.element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      hideInspectOverlay();
      return;
    }

    if (!inspectOverlay) {
      inspectOverlay = document.createElement('div');
      inspectOverlay.setAttribute('data-rs-bb-devtools-inspect-overlay', 'true');
      inspectOverlay.style.position = 'fixed';
      inspectOverlay.style.zIndex = '2147483646';
      inspectOverlay.style.pointerEvents = 'none';
      inspectOverlay.style.border = '2px solid #2f6fbc';
      inspectOverlay.style.borderRadius = '6px';
      inspectOverlay.style.background = 'rgba(47, 111, 188, 0.1)';
      inspectOverlay.style.boxShadow = '0 0 0 3px rgba(47, 111, 188, 0.16)';
      inspectOverlay.style.color = '#fff';
      inspectOverlay.style.font = '700 11px/1 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      inspectOverlay.style.padding = '4px 6px';
      inspectOverlay.style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.5)';
      inspectOverlay.style.overflow = 'hidden';
      inspectOverlay.style.whiteSpace = 'nowrap';
      inspectOverlay.style.textOverflow = 'ellipsis';
      document.documentElement.appendChild(inspectOverlay);
    }

    inspectOverlay.textContent = inspectTargetLabel(target);
    inspectOverlay.style.display = 'block';
    inspectOverlay.style.left = rect.left + 'px';
    inspectOverlay.style.top = rect.top + 'px';
    inspectOverlay.style.width = rect.width + 'px';
    inspectOverlay.style.height = rect.height + 'px';
  }

  function hideInspectOverlay() {
    if (!inspectOverlay) return;

    inspectOverlay.remove();
    inspectOverlay = null;
  }

  function renderActiveTab(data) {
    if (activeTab === 'overview') return renderOverview(data);
    if (activeTab === 'metrics') return renderMetrics(data.metrics || {});
    if (activeTab === 'fields') return renderFields(data.metrics || {});
    if (activeTab === 'sections') return renderSections(data.metrics || {});
    if (activeTab === 'saves') return renderSaves(data.saves || []);
    if (activeTab === 'scores') return renderScores(data.latest_processed || {}, data.metrics || {}, data.saves || []);
    if (activeTab === 'raw') return renderRawPayload(data.metrics || {});
    if (activeTab === 'config') return renderConfig(data.config || {});
    return renderOverview(data);
  }
}

function renderShell() {
  return `
    <style>${styles()}</style>
    <button class="rs-bb-devtools-button" type="button" data-rs-bb-devtools-action="toggle">BB</button>
    <section class="rs-bb-devtools-panel" aria-label="RiskSeal BB DevTools">
      <div class="rs-bb-devtools-header"></div>
      <div class="rs-bb-devtools-main">
        <nav class="rs-bb-devtools-tabs">${renderTabs('overview')}</nav>
        <main class="rs-bb-devtools-body"></main>
      </div>
    </section>
  `;
}

function renderHeader(data, inspectMode) {
  const inspectActive = inspectMode ? ' active' : '';

  return `
    <div class="rs-bb-devtools-title">RiskSeal BB DevTools</div>
    <div class="rs-bb-devtools-header-actions">
      <button
        type="button"
        class="rs-bb-devtools-inspect${inspectActive}"
        data-rs-bb-devtools-action="toggle-inspect"
        aria-label="${inspectMode ? 'Disable selector mode' : 'Enable selector mode'}"
      >${searchIcon()}</button>
      <div class="rs-bb-devtools-segmented" aria-label="Panel position">
        ${PANEL_POSITIONS.map(function (position) {
          const active = position === panelPosition ? ' active' : '';
          return `<button type="button" class="rs-bb-devtools-segment${active}" data-rs-bb-devtools-action="set-panel-position" data-rs-bb-devtools-position="${position}">${position}</button>`;
        }).join('')}
      </div>
      <button type="button" class="rs-bb-devtools-reset" data-rs-bb-devtools-action="reset-session">Reset session</button>
      <button type="button" class="rs-bb-devtools-close" data-rs-bb-devtools-action="close">Close</button>
    </div>
  `;
}

function searchIcon() {
  return '<svg class="rs-bb-devtools-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.75" cy="10.75" r="6.25"></circle><path d="M15.5 15.5L20 20"></path></svg>';
}

function renderTabs(activeTab) {
  let html = '';
  for (let i = 0; i < TABS.length; i++) {
    const tab = TABS[i];
    const active = tab.id === activeTab ? ' active' : '';
    html += `<button type="button" class="rs-bb-devtools-tab${active}" data-rs-bb-devtools-tab="${tab.id}">${tab.label}</button>`;
  }

  return html;
}

function renderOverview(data) {
  const debug = data.debug || {};
  const config = data.config || {};
  const warnings = debug.warnings || [];
  const sessionId = debug.session_id || 'none';

  return `
    <section class="rs-bb-devtools-card">
      <h3>Integration</h3>
      ${detailRow('App ID', config.app_id || 'not configured')}
      ${detailRow('Mode', debug.mode || config.mode || 'unknown')}
      ${detailRow('Session', sessionId)}
    </section>
    <div class="rs-bb-devtools-grid">
      ${statCard('Lifecycle', debug.lifecycle || 'idle')}
      ${statCard('Last save', debug.last_save_status || 'none')}
      ${statCard('Forms', debug.forms_detected || 0)}
      ${statCard('Fields', debug.fields_detected || 0)}
      ${statCard('Sections', debug.sections_detected || 0)}
      ${statCard('Saves', data.saves ? data.saves.length : 0)}
    </div>
    <section class="rs-bb-devtools-card">
      <h3>Warnings</h3>
      ${
        warnings.length
          ? `<div class="rs-bb-devtools-tags">${warnings.map(warningTag).join('')}</div>`
          : '<div class="rs-bb-devtools-empty">No warnings</div>'
      }
    </section>
  `;
}

function renderMetrics(metrics) {
  const rows = [];
  flattenMetrics(metrics, '', rows);
  const scoreContext = createScoreLabelContext(metrics);
  const filtered = filterRows(rows, scoreContext);
  const groups = groupByCategory(filtered);
  const categoryNames = Object.keys(groups);
  const pathsCollapsed = metricsPathsCollapsed;
  const groupsToggleLabel = areAllMetricGroupsCollapsed(categoryNames) ? 'Expand all' : 'Collapse all';

  return `
    <div class="rs-bb-devtools-toolbar">
      <input class="rs-bb-devtools-search" data-rs-bb-devtools-search="true" value="${escapeHtml(searchValue())}" placeholder="Search metrics..." />
      ${renderScoreFilterControls()}
      <button type="button" class="rs-bb-devtools-copy" data-rs-bb-devtools-action="toggle-metric-paths">
        ${pathsCollapsed ? 'Show paths' : 'Hide paths'}
      </button>
      <button type="button" class="rs-bb-devtools-copy" data-rs-bb-devtools-action="toggle-metric-groups-all">${groupsToggleLabel}</button>
      <button type="button" class="rs-bb-devtools-copy" data-rs-bb-devtools-action="copy">Copy JSON</button>
    </div>
    <div class="${pathsCollapsed ? 'rs-bb-devtools-metrics paths-collapsed' : 'rs-bb-devtools-metrics'}">
      ${
        categoryNames.length
          ? categoryNames
              .map(function (category) {
                return metricGroup(category, groups[category], pathsCollapsed, scoreContext);
              })
              .join('')
          : '<div class="rs-bb-devtools-empty">No metrics captured yet</div>'
      }
    </div>
  `;
}

function renderFields(metrics) {
  const fields = collectFields(metrics);
  const scoreContext = createScoreLabelContext(metrics);
  const filtered = filterFields(fields, scoreContext);

  if (!fields.length) return '<div class="rs-bb-devtools-empty">No fields captured yet</div>';

  return `
    <div class="rs-bb-devtools-toolbar">
      ${renderFieldCategoryFilterControls()}
      ${renderScoreFilterControls()}
    </div>
    ${
      filtered.length
        ? `<div class="rs-bb-devtools-field-list">${filtered
            .map(function (item) {
              return renderFieldCard(item, scoreContext);
            })
            .join('')}</div>`
        : '<div class="rs-bb-devtools-empty">No fields match current filters</div>'
    }
  `;
}

function renderSections(metrics) {
  const sections = collectSections(metrics);
  const scoreContext = createScoreLabelContext(metrics);
  const filtered = filterSections(sections, scoreContext);

  if (!sections.length) return '<div class="rs-bb-devtools-empty">No sections captured yet</div>';

  return `
    <div class="rs-bb-devtools-toolbar">
      ${renderScoreFilterControls()}
    </div>
    ${
      filtered.length
        ? `<div class="rs-bb-devtools-section-list">${filtered
            .map(function (item) {
              return renderSectionCard(item, scoreContext);
            })
            .join('')}</div>`
        : '<div class="rs-bb-devtools-empty">No sections match current filters</div>'
    }
  `;
}

function renderSaves(saves) {
  if (!saves.length) return '<div class="rs-bb-devtools-empty">No save attempts yet</div>';

  return `
    <div class="rs-bb-devtools-save-list">
      ${saves.map(renderSaveTimeline).join('')}
    </div>
  `;
}

function renderScores(processed, metrics, saves) {
  const scores = findScores(processed.data);
  const contributorMetrics =
    findProcessedMetrics(processed.data) || findSaveMetricsForProcessed(saves, processed) || metrics || {};
  const contributorsByScore = collectScoreContributors(contributorMetrics);

  if (processed.status === 'loading') return '<div class="rs-bb-devtools-empty">Loading processed session...</div>';
  if (processed.status === 'auth_required')
    return `<div class="rs-bb-devtools-empty">${escapeHtml(processed.error || 'Processed session requires x-api-key')}</div>`;
  if (processed.status === 'error')
    return `<div class="rs-bb-devtools-empty">Processed fetch failed: ${escapeHtml(processed.error || '')}</div>`;
  if (!scores) return '<div class="rs-bb-devtools-empty">No processed scores loaded yet</div>';

  const scoresToggleLabel = areAllScorePanelsCollapsed() ? 'Expand all' : 'Collapse all';

  return `
    <div class="rs-bb-devtools-toolbar">
      <button type="button" class="rs-bb-devtools-copy" data-rs-bb-devtools-action="toggle-score-panels-all">${scoresToggleLabel}</button>
    </div>
    <section class="rs-bb-devtools-card">
      <h3>Scores after save</h3>
      ${SCORE_KEYS.map(function (key) {
        return scorePanel(key, scores[key], contributorsByScore[key] || []);
      }).join('')}
    </section>
    <section class="rs-bb-devtools-card">
      <div class="rs-bb-devtools-card-heading">
        <div>
          <h3>Processed response</h3>
        </div>
        <button type="button" class="rs-bb-devtools-copy" data-rs-bb-devtools-action="copy-processed-response">Copy JSON</button>
      </div>
      <pre data-rs-bb-devtools-scroll-key="processed-response">${escapeHtml(JSON.stringify(processed.data || {}, null, 2))}</pre>
    </section>
  `;
}

function renderRawPayload(metrics) {
  return `
    <div class="rs-bb-devtools-toolbar">
      <button type="button" class="rs-bb-devtools-copy" data-rs-bb-devtools-action="copy">Copy JSON</button>
    </div>
    <pre data-rs-bb-devtools-scroll-key="raw">${escapeHtml(JSON.stringify(metrics || {}, null, 2))}</pre>
  `;
}

function renderConfig(config) {
  return `
    <section class="rs-bb-devtools-card">
      <h3>Effective script config</h3>
      ${detailRow('App ID', config.app_id || 'not configured')}
      ${detailRow('API host', config.api_host || 'default')}
      ${detailRow('Mode', config.mode || 'auto')}
      ${detailRow('Attributes mode', config.attributes_mode || 'merge')}
      ${detailRow('Timeout', config.timeout || 'default')}
      ${detailRow('Hold submit timeout', config.hold_submit_timeout || 'default')}
      ${detailRow('Debug UI', config.debug_enabled ? 'enabled' : 'disabled')}
      ${detailRow('Processed API key', config.debug_api_key_configured ? 'configured' : 'not configured')}
      ${detailRow('Script src', config.script_src || 'not detected')}
    </section>
  `;
}

function statCard(label, value) {
  return `
    <div class="rs-bb-devtools-stat">
      <div class="rs-bb-devtools-stat-label">${escapeHtml(label)}</div>
      <div class="rs-bb-devtools-stat-value">${escapeHtml(value)}</div>
    </div>
  `;
}

function warningTag(warning) {
  return `<span class="rs-bb-devtools-tag">${escapeHtml(warning)}</span>`;
}

function renderScoreFilterControls() {
  const activeScore = activeScoreFilterValue();

  return `
    <label class="rs-bb-devtools-filter-group">
      <span class="rs-bb-devtools-filter-label">Score</span>
      <select class="rs-bb-devtools-filter-select" data-rs-bb-devtools-score-filter-select="true" aria-label="Score filter">
        <option value=""${selectedAttribute(activeScore === '')}>All</option>
      ${SCORE_KEYS.map(function (score) {
        return `<option value="${escapeHtml(score)}"${selectedAttribute(activeScore === score)}>${escapeHtml(scoreLabel(score))}</option>`;
      }).join('')}
      </select>
    </label>
  `;
}

function renderFieldCategoryFilterControls() {
  const activeCategory = activeFieldCategoryFilterValue();

  return `
    <label class="rs-bb-devtools-filter-group">
      <span class="rs-bb-devtools-filter-label">Field type</span>
      <select
        class="rs-bb-devtools-filter-select"
        data-rs-bb-devtools-field-category-filter-select="true"
        aria-label="Field category filter"
      >
        <option value=""${selectedAttribute(activeCategory === '')}>All</option>
      ${FIELD_FILTER_KEYS.map(function (category) {
        return `<option value="${escapeHtml(category)}"${selectedAttribute(activeCategory === category)}>${escapeHtml(category)}</option>`;
      }).join('')}
      </select>
    </label>
  `;
}

function selectedAttribute(selected) {
  return selected ? ' selected' : '';
}

function scoreLabel(score) {
  return String(score || '').replace(/_/g, ' ');
}

function areAllMetricGroupsCollapsed(categories) {
  if (!categories || !categories.length) return true;

  for (let i = 0; i < categories.length; i += 1) {
    if (!isMetricGroupCollapsed(categories[i])) return false;
  }

  return true;
}

function isMetricGroupCollapsed(category) {
  return metricGroupsCollapsed[category] !== false;
}

function metricGroup(category, rows, pathsCollapsed, scoreContext) {
  const collapsed = isMetricGroupCollapsed(category);
  const countLabel = rows.length === 1 ? '1 metric' : rows.length + ' metrics';

  return `
    <section class="${collapsed ? 'rs-bb-devtools-card rs-bb-devtools-card-collapsed' : 'rs-bb-devtools-card'}">
      <div class="rs-bb-devtools-card-heading">
        <div>
          <h3>${escapeHtml(category)}</h3>
          <span>${escapeHtml(countLabel)}</span>
        </div>
        <button
          type="button"
          class="${collapsed ? 'rs-bb-devtools-icon-button collapsed' : 'rs-bb-devtools-icon-button'}"
          data-rs-bb-devtools-action="toggle-metric-group"
          data-rs-bb-devtools-metric-group="${escapeHtml(category)}"
          aria-expanded="${collapsed ? 'false' : 'true'}"
          aria-label="${collapsed ? 'Expand ' : 'Collapse '}${escapeHtml(category)} metrics"
        >
          <span class="rs-bb-devtools-chevron" aria-hidden="true"></span>
        </button>
      </div>
      ${
        collapsed
          ? ''
          : `<table class="rs-bb-devtools-table compact">
              <tbody>
                ${rows
                  .map(function (row) {
                    return metricRow(row, pathsCollapsed, scoreContext);
                  })
                  .join('')}
              </tbody>
            </table>`
      }
    </section>
  `;
}

function metricRow(row, pathsCollapsed, scoreContext, scorePath) {
  const scoreBadges = metricScoreBadges(scorePath || row.path, scoreContext);

  if (pathsCollapsed) {
    return `
      <tr>
        <td class="rs-bb-devtools-value collapsed" colspan="2">
          <span class="rs-bb-devtools-metric-key" title="${escapeHtml(row.path)}">
            ${escapeHtml(shortMetricPath(row.path))}
            ${scoreBadges}
          </span>
          <span>${escapeHtml(formatValue(row.value))}</span>
        </td>
      </tr>
    `;
  }

  return `
    <tr>
      <td class="rs-bb-devtools-path">
        ${escapeHtml(row.path)}
        ${scoreBadges}
      </td>
      <td class="rs-bb-devtools-value">${escapeHtml(formatValue(row.value))}</td>
    </tr>
  `;
}

function metricScoreBadges(path, scoreContext) {
  const scores = scoreTagsForPath(path, scoreContext);
  if (!scores.length) return '';

  return `<span class="rs-bb-devtools-score-tags">${scores
    .map(function (score) {
      return `<span class="rs-bb-devtools-score-tag">${escapeHtml(score)}</span>`;
    })
    .join('')}</span>`;
}

function scoreTagsForPath(path, scoreContext) {
  const contributors = scoreContributorsForPath(path, scoreContext);
  const scores = [];

  for (let i = 0; i < contributors.length; i += 1) {
    if (scores.indexOf(contributors[i].score) === -1) scores.push(contributors[i].score);
  }

  return scores;
}

function fieldCategoryBadges(item, scoreContext) {
  const categories = resolvedFieldCategories(item, scoreContext);

  return `<span class="rs-bb-devtools-field-categories">${categories
    .map(function (category) {
      return `<span class="rs-bb-devtools-field-category category-${escapeClass(category)}">${escapeHtml(category)}</span>`;
    })
    .join('')}</span>`;
}

function sectionCategoryBadges(item, scoreContext) {
  const categories = resolvedSectionCategories(item, scoreContext);

  if (!categories.length) return '';

  return `<span class="rs-bb-devtools-field-categories">${categories
    .map(function (category) {
      return `<span class="rs-bb-devtools-field-category category-${escapeClass(category)}">${escapeHtml(category)}</span>`;
    })
    .join('')}</span>`;
}

function resolvedFieldCategories(item, scoreContext) {
  const key = [item.pagePath || 'pages.' + item.pageId, item.formId, item.fieldId].join('::');
  const categories = scoreContext && scoreContext.fieldCategories ? scoreContext.fieldCategories[key] : null;

  return categories && categories.length ? categories : ['unrecognized'];
}

function resolvedSectionCategories(item, scoreContext) {
  const key = [item.pagePath || 'pages.' + item.pageId, item.sectionId].join('::');
  const categories = scoreContext && scoreContext.sectionCategories ? scoreContext.sectionCategories[key] : null;

  return categories || [];
}

function nameMatchesCategory(value, tokens) {
  const lower = String(value || '').toLowerCase();

  for (let i = 0; i < tokens.length; i += 1) {
    if (lower === tokens[i] || lower.indexOf(tokens[i]) !== -1) return true;
  }

  return false;
}

function uniqueValues(values) {
  const out = [];

  for (let i = 0; i < values.length; i += 1) {
    if (out.indexOf(values[i]) === -1) out.push(values[i]);
  }

  return out;
}

function renderFieldCard(item, scoreContext) {
  const field = item.field || {};
  const keystroke = field.keystroke || {};
  const corrections = field.corrections || {};
  const rows = [];
  const detailsKey = fieldDetailsKey(item);
  const detailsOpen = fieldDetailsOpen[detailsKey] ? ' open' : '';
  const selected = isInspectSelection('field', detailsKey) ? ' selected' : '';
  flattenMetrics(field, '', rows);
  const summary =
    field.type === 'file'
      ? `
        ${statCard('Changes', formatNumber(field.changes_count, '') || '0')}
        ${statCard('Files last', formatNumber(field.files_count_last, '') || '0')}
        ${statCard('Files max', formatNumber(field.files_count_max, '') || '0')}
        ${statCard('Multiple', field.multiple_enabled ? 'Yes' : 'No')}
        ${statCard('Select avg', formatNumber(field.selection_latency_avg_ms, 'ms') || '0')}
        ${statCard('Cancels', formatNumber(field.cancel_count, '') || '0')}
      `
      : `
        ${statCard('Focus', formatNumber(field.focus_duration_ms, 'ms') || '0')}
        ${statCard('Keys', formatNumber(keystroke.total_count, '') || '0')}
        ${statCard('Corrections', formatNumber(corrections.backspace_count, '') || '0')}
        ${statCard('Paste', formatNumber(field.paste_count, '') || '0')}
        ${statCard('Hover', formatNumber(field.pause_before_focus_ms, 'ms') || '0')}
      `;

  return `
    <section class="rs-bb-devtools-card rs-bb-devtools-field-card${selected}" data-rs-bb-devtools-field-card-key="${escapeHtml(detailsKey)}" tabindex="-1">
      <div class="rs-bb-devtools-field-heading">
        <div>
          <h3>${escapeHtml(field.name || item.fieldId)}</h3>
          <div class="rs-bb-devtools-field-subtitle">
            form: ${escapeHtml(item.formId)}
            <span>page: ${escapeHtml(item.pageId)}</span>
            <span>type: ${escapeHtml(field.type || '')}</span>
            ${fieldCategoryBadges(item, scoreContext)}
          </div>
        </div>
      </div>
      <div class="rs-bb-devtools-field-summary">
        ${summary}
      </div>
      <details class="rs-bb-devtools-details" data-rs-bb-devtools-field-key="${escapeHtml(detailsKey)}"${detailsOpen}>
        <summary>All field metrics</summary>
        <table class="rs-bb-devtools-table compact">
          <tbody>
            ${rows
              .map(function (row) {
                return metricRow(row, false, scoreContext, fieldMetricFullPath(item, row.path));
              })
              .join('')}
          </tbody>
        </table>
      </details>
    </section>
  `;
}

function fieldDetailsKey(item) {
  return [item.pageId, item.formId, item.fieldId].join('::');
}

function sectionDetailsKey(item) {
  return [item.pageId, item.sectionId].join('::');
}

function fieldMetricFullPath(item, localPath) {
  return ['pages', item.pageId, 'forms', item.formId, 'fields', item.fieldId, localPath].join('.');
}

function sectionMetricFullPath(item, localPath) {
  return ['pages', item.pageId, 'sections', item.sectionId, localPath].join('.');
}

function isInspectSelection(type, key) {
  return !!inspectSelection && inspectSelection.type === type && inspectSelection.key === key;
}

function resolveInspectTarget(target, x, y) {
  const candidates = [];
  if (target && target.nodeType === 1) candidates.push(target);
  if (typeof document !== 'undefined' && Number.isFinite(x) && Number.isFinite(y) && document.elementsFromPoint) {
    const pointElements = document.elementsFromPoint(x, y);
    for (let i = 0; i < pointElements.length; i += 1) {
      if (candidates.indexOf(pointElements[i]) === -1) candidates.push(pointElements[i]);
    }
  }

  for (let i = 0; i < candidates.length; i += 1) {
    const resolved = resolveInspectCandidate(candidates[i]);
    if (resolved) return resolved;
  }

  return null;
}

function resolveInspectCandidate(target) {
  const field = resolveField(target);
  if (field) {
    return {
      type: 'field',
      pageId: currentPageKey(),
      formId: field.formId,
      fieldId: field.fieldName,
      element: field.element,
    };
  }

  const section = resolveSection(target);
  if (section) {
    return {
      type: 'section',
      pageId: currentPageKey(),
      sectionId: section.id,
      element: section.element,
    };
  }

  return null;
}

function inspectTargetKey(target) {
  if (target.type === 'field') return [target.pageId, target.formId, target.fieldId].join('::');
  return [target.pageId, target.sectionId].join('::');
}

function inspectTargetLabel(target) {
  if (!target) return '';
  if (target.type === 'field') return 'Field: ' + target.fieldId + ' / ' + target.formId;
  return 'Section: ' + target.sectionId;
}

function renderSectionCard(item, scoreContext) {
  const section = item.section || {};
  const scroll = section.scroll || {};
  const rows = [];
  const detailsKey = sectionDetailsKey(item);
  const detailsOpen = sectionDetailsOpen[detailsKey] ? ' open' : '';
  const selected = isInspectSelection('section', detailsKey) ? ' selected' : '';
  flattenMetrics(section, '', rows);

  return `
    <section class="rs-bb-devtools-card rs-bb-devtools-section-card${selected}" data-rs-bb-devtools-section-card-key="${escapeHtml(detailsKey)}" tabindex="-1">
      <div class="rs-bb-devtools-section-heading">
        <div>
          <h3>${escapeHtml(item.sectionId)}</h3>
          <div class="rs-bb-devtools-section-subtitle">
            page: ${escapeHtml(item.pageId)}
            ${sectionCategoryBadges(item, scoreContext)}
          </div>
        </div>
      </div>
      <div class="rs-bb-devtools-section-summary">
        ${statCard('Visits', formatNumber(section.visits_count, '') || '0')}
        ${statCard('Time', formatNumber(section.time_total_ms, 'ms') || '0')}
        ${statCard('Pauses', formatNumber(section.pause_count, '') || '0')}
        ${statCard('Depth', formatNumber(scroll.depth_max, '') || '0')}
        ${statCard('Speed', formatNumber(scroll.max_speed, 'px/ms') || '0')}
        ${statCard('Selection', formatNumber(section.selection_count, '') || '0')}
      </div>
      <details class="rs-bb-devtools-details" data-rs-bb-devtools-section-key="${escapeHtml(detailsKey)}"${detailsOpen}>
        <summary>All section metrics</summary>
        <table class="rs-bb-devtools-table compact">
          <tbody>
            ${rows
              .map(function (row) {
                return metricRow(row, false, scoreContext, sectionMetricFullPath(item, row.path));
              })
              .join('')}
          </tbody>
        </table>
      </details>
    </section>
  `;
}

function renderSaveTimeline(save) {
  const scores = findScores(save.processed);
  const details = saveDetails(save);
  const payloadKey = savePayloadDetailsKey(save);
  const payloadOpen = savePayloadDetailsOpen[payloadKey] === true;

  return `
    <section class="rs-bb-devtools-card rs-bb-devtools-save-card">
      <div class="rs-bb-devtools-save-heading">
        <div>
          <h3>Save #${escapeHtml(save.id)}</h3>
          <div class="rs-bb-devtools-save-meta">
            ${escapeHtml(formatTime(save.started_at))}
            <span>session: ${escapeHtml(shortId(save.session_id || 'none'))}</span>
            <span>duration: ${escapeHtml(formatNumber(save.duration_ms, 'ms') || 'pending')}</span>
          </div>
        </div>
        ${warningTag(save.status || 'idle')}
      </div>
      <div class="rs-bb-devtools-timeline">
        ${timelineStep('capture', 'Captured payload', save.sent_payload ? 'ready' : 'idle', 'Metrics snapshot prepared before save.')}
        ${timelineStep('save', 'Save request', saveRequestState(save), saveRequestDetail(save))}
        ${timelineStep('processed', 'Processed fetch', processedState(save), processedDetail(save))}
        ${timelineStep('scores', 'Scores', scores ? 'ready' : processedState(save), scores ? 'Scores loaded from processed session.' : 'Waiting for processed scores.')}
      </div>
      ${details ? `<div class="rs-bb-devtools-save-error">${escapeHtml(details)}</div>` : ''}
      <div class="rs-bb-devtools-details" data-rs-bb-devtools-save-payload-key="${escapeHtml(payloadKey)}">
        <button
          type="button"
          class="rs-bb-devtools-summary"
          data-rs-bb-devtools-action="toggle-save-payload"
          data-rs-bb-devtools-save-payload-key="${escapeHtml(payloadKey)}"
          aria-expanded="${payloadOpen ? 'true' : 'false'}"
        >${payloadOpen ? '▾' : '▸'} Latest sent payload</button>
        ${
          payloadOpen
            ? `
        <div class="rs-bb-devtools-toolbar">
          <button
            type="button"
            class="rs-bb-devtools-copy"
            data-rs-bb-devtools-action="copy-save-payload"
            data-rs-bb-devtools-save-payload-key="${escapeHtml(payloadKey)}"
          >Copy JSON</button>
        </div>
        <pre data-rs-bb-devtools-scroll-key="${escapeHtml('save-payload-' + payloadKey)}">${escapeHtml(JSON.stringify(save.sent_payload || {}, null, 2))}</pre>
        `
            : ''
        }
      </div>
    </section>
  `;
}

function savePayloadDetailsKey(save) {
  return [save.id, save.session_id || 'none'].join('::');
}

function timelineStep(key, label, state, detail) {
  return `
    <div class="rs-bb-devtools-timeline-step state-${escapeClass(state)}">
      <div class="rs-bb-devtools-timeline-dot"></div>
      <div>
        <div class="rs-bb-devtools-timeline-label">${escapeHtml(label)}</div>
        <div class="rs-bb-devtools-timeline-detail">${escapeHtml(detail || key)}</div>
      </div>
    </div>
  `;
}

function saveRequestState(save) {
  if (save.hold_timeout) return 'warning';
  if (save.status === 'ok') return 'ready';
  if (save.status === 'saving') return 'loading';
  if (save.status === 'timeout') return 'warning';
  if (save.status === 'error') return 'error';
  return 'idle';
}

function saveRequestDetail(save) {
  if (save.hold_timeout && save.save_status === 'ok') return 'Hold timeout elapsed; BB save completed later.';
  if (save.hold_timeout && save.save_status === 'error') {
    return save.error
      ? `Hold timeout elapsed; BB save failed later: ${save.error}`
      : 'Hold timeout elapsed; BB save failed later.';
  }
  if (save.hold_timeout) return 'Hold timeout elapsed before completion.';
  if (save.error) return save.error;
  if (save.status === 'ok') return 'BB save completed.';
  if (save.status === 'saving') return 'Request is in progress.';
  if (save.status === 'timeout') return 'Hold timeout elapsed before completion.';
  return 'No request result yet.';
}

function processedState(save) {
  if (save.processed_status === 'ok') return 'ready';
  if (save.processed_status === 'loading') return 'loading';
  if (save.processed_status === 'auth_required') return 'error';
  if (save.processed_status === 'error') return 'error';
  return 'idle';
}

function processedDetail(save) {
  if (save.processed_status === 'ok') return 'Processed session loaded.';
  if (save.processed_status === 'loading') return 'Fetching processed session, retrying while backend catches up.';
  if (save.processed_status === 'auth_required') return save.processed_error || 'No debug API key configured.';
  if (save.processed_status === 'error') return save.processed_error || 'Processed fetch failed.';
  return 'Processed fetch has not started.';
}

function scoreBar(key, value) {
  const numeric = Number(value);
  const safeValue = Number.isFinite(numeric) ? Math.max(0, Math.min(1, numeric)) : 0;
  const width = Math.round(safeValue * 100);

  return `
    <div class="rs-bb-devtools-score">
      <div class="rs-bb-devtools-score-label">${escapeHtml(key)}</div>
      <div class="rs-bb-devtools-score-track"><div style="width: ${width}%"></div></div>
      <div class="rs-bb-devtools-score-value">${escapeHtml(safeValue.toFixed(3))}</div>
    </div>
  `;
}

function areAllScorePanelsCollapsed() {
  for (let i = 0; i < SCORE_KEYS.length; i += 1) {
    if (!isScorePanelCollapsed(SCORE_KEYS[i])) return false;
  }

  return true;
}

function isScorePanelCollapsed(key) {
  return scorePanelsCollapsed[key] !== false;
}

function scorePanel(key, value, contributors) {
  const collapsed = isScorePanelCollapsed(key);

  return `
    <div class="${collapsed ? 'rs-bb-devtools-score-panel collapsed' : 'rs-bb-devtools-score-panel'}">
      <div class="rs-bb-devtools-score-panel-heading">
        ${scoreBar(key, value)}
        <button
          type="button"
          class="${collapsed ? 'rs-bb-devtools-icon-button collapsed' : 'rs-bb-devtools-icon-button'}"
          data-rs-bb-devtools-action="toggle-score-panel"
          data-rs-bb-devtools-score="${escapeHtml(key)}"
          aria-expanded="${collapsed ? 'false' : 'true'}"
          aria-label="${collapsed ? 'Expand ' : 'Collapse '}${escapeHtml(key)} contributors"
        >
          <span class="rs-bb-devtools-chevron" aria-hidden="true"></span>
        </button>
      </div>
      ${
        collapsed
          ? ''
          : contributors.length
            ? `<table class="rs-bb-devtools-table compact rs-bb-devtools-contributors">
              <tbody>
                ${contributors.map(scoreContributorRow).join('')}
              </tbody>
            </table>`
            : '<div class="rs-bb-devtools-muted">No contributor metrics captured yet</div>'
      }
    </div>
  `;
}

function scoreContributorRow(item) {
  return `
    <tr>
      <td>${escapeHtml(item.component)}</td>
      <td class="rs-bb-devtools-path" title="${escapeHtml(item.path)}">${escapeHtml(item.path)}</td>
      <td class="rs-bb-devtools-value">${escapeHtml(formatValue(item.value))}</td>
    </tr>
  `;
}

function collectScoreContributors(metrics) {
  const rows = [];
  const result = {};
  const seen = {};
  const scoreContext = createScoreLabelContext(metrics || {});
  flattenMetrics(metrics || {}, '', rows);

  for (let r = 0; r < rows.length; r += 1) {
    const row = rows[r];
    const contributors = scoreContributorsForPath(row.path, scoreContext);

    for (let i = 0; i < contributors.length; i += 1) {
      const contributor = contributors[i];
      const seenKey = contributor.score + '::' + contributor.component + '::' + row.path;
      if (seen[seenKey]) continue;
      seen[seenKey] = true;
      if (!result[contributor.score]) result[contributor.score] = [];
      result[contributor.score].push({
        component: contributor.component,
        path: row.path,
        value: row.value,
      });
    }
  }

  return result;
}

function findSaveMetricsForProcessed(saves, processed) {
  if (!saves || !saves.length) return null;

  const sessionId = processed && processed.session_id ? processed.session_id : null;
  if (sessionId) {
    for (let i = 0; i < saves.length; i += 1) {
      if (saves[i] && saves[i].session_id === sessionId) return saveMetrics(saves[i]);
    }
  }

  for (let i = 0; i < saves.length; i += 1) {
    const metrics = saveMetrics(saves[i]);
    if (metrics && typeof metrics === 'object') return metrics;
  }

  return null;
}

function saveMetrics(save) {
  const metrics = save && save.sent_payload && save.sent_payload.metrics;
  return metrics && typeof metrics === 'object' ? metrics : null;
}

function scoreContributorsForPath(path, scoreContext) {
  const contributors = [];

  addScoreContributor(
    contributors,
    isFieldMetricInCategory(path, 'pause_before_input_ms', ['sensitive'], scoreContext),
    'cognitive_load',
    'first_key_latency'
  );
  addScoreContributor(
    contributors,
    isFieldMetricInCategory(path, 'keystroke.typing_rhythm.flight_var_ms2', ['sensitive'], scoreContext),
    'cognitive_load',
    'iki_std'
  );
  addScoreContributor(
    contributors,
    isFieldMetricInCategory(path, 'empty_focus_count', ['sensitive'], scoreContext),
    'cognitive_load',
    'empty_focus'
  );
  addScoreContributor(contributors, isFieldMetric(path, 'focus_duration_ms'), 'cognitive_load', 'dwell_outlier');
  addScoreContributor(
    contributors,
    isFieldMetric(path, 'revision_count') || isFieldMetric(path, 'changes_count'),
    'hesitation',
    're_edit'
  );
  addScoreContributor(
    contributors,
    isFieldMetric(path, 'abandonment_and_return_count'),
    'hesitation',
    'abandonment_return'
  );
  addScoreContributor(
    contributors,
    isSessionMetric(path, 'pause_before_submit_ms') || isFieldMetric(path, 'pause_before_input_ms'),
    'hesitation',
    'decision_pause'
  );
  addScoreContributor(
    contributors,
    isFieldMetric(path, 'returned_to_original_count'),
    'hesitation',
    'returned_to_original'
  );

  addScoreContributor(
    contributors,
    isFieldMetricInCategory(path, 'keystroke.flight_time_avg_ms', ['personal'], scoreContext),
    'digital_fluency',
    'iki_norm_inverse'
  );
  addScoreContributor(
    contributors,
    isFieldMetricInCategory(path, 'corrections.backspace_count', ['personal'], scoreContext) ||
      isFieldMetricInCategory(path, 'corrections.del_count', ['personal'], scoreContext) ||
      isFieldMetricInCategory(path, 'keystroke.total_count', ['personal'], scoreContext),
    'digital_fluency',
    'backspace_inverse'
  );
  addScoreContributor(
    contributors,
    isFieldMetricInCategory(path, 'paste_count', ['personal', 'sensitive'], scoreContext),
    'digital_fluency',
    'no_paste'
  );

  addScoreContributor(contributors, path === 'session.duration_ms', 'impulsivity', 'duration_inverse');
  addScoreContributor(
    contributors,
    isFirstSectionMetric(path, 'terms', 'scroll.depth_max', scoreContext) ||
      isFirstSectionMetric(path, 'terms', 'scroll.max_speed', scoreContext),
    'impulsivity',
    'tc_scroll_risk'
  );
  addScoreContributor(
    contributors,
    isAmountRangeFieldMetric(path, 'max_amt_flag', scoreContext),
    'impulsivity',
    'max_amt_flag'
  );
  addScoreContributor(contributors, isRoundedNumberMetric(path, scoreContext), 'impulsivity', 'round_numbers');

  addScoreContributor(
    contributors,
    path === 'session.sessions_lifetime' || path === 'session.active_days_lifetime',
    'research_intent',
    'prior_sessions'
  );
  addScoreContributor(
    contributors,
    isAmountRangeFieldMetric(path, 'changes_count', scoreContext),
    'research_intent',
    'amount_explore'
  );
  addScoreContributor(
    contributors,
    isFirstSectionMetric(path, 'schedule', 'time_total_ms', scoreContext),
    'research_intent',
    'schedule_dwell'
  );
  addScoreContributor(
    contributors,
    isFirstSectionMetric(path, 'faq', 'visits_count', scoreContext),
    'research_intent',
    'help_visits'
  );

  return contributors;
}

function addScoreContributor(contributors, condition, score, component) {
  if (!condition) return;
  contributors.push({ score, component });
}

function createScoreLabelContext(metrics) {
  const context = {
    attributesMode: readAttributesMode(metrics),
    fieldTypes: {},
    fieldCategories: {},
    sectionCategories: {},
    firstSections: {},
  };
  const pages = metrics && metrics.pages ? metrics.pages : {};
  const pageIds = Object.keys(pages);

  for (let p = 0; p < pageIds.length; p += 1) {
    const pageId = pageIds[p];
    const pagePath = 'pages.' + pageId;
    const page = pages[pageId] || {};
    const forms = page.forms || {};
    const formIds = Object.keys(forms);

    for (let f = 0; f < formIds.length; f += 1) {
      const formId = formIds[f];
      const fields = forms[formId] && forms[formId].fields ? forms[formId].fields : {};
      const fieldIds = Object.keys(fields);

      for (let i = 0; i < fieldIds.length; i += 1) {
        const fieldId = fieldIds[i];
        const field = fields[fieldId] || {};
        const fieldKey = [pagePath, formId, fieldId].join('::');
        context.fieldTypes[fieldKey] = typeof field.type === 'string' ? field.type : null;
        context.fieldCategories[fieldKey] = resolveFieldCategoryList(fieldId, field, context.attributesMode);
      }
    }

    collectFirstSectionsForPage(context, pagePath, page.sections || {});
  }

  return context;
}

function collectFirstSectionsForPage(context, pagePath, sections) {
  const sectionIds = Object.keys(sections || {});
  const categories = Object.keys(SECTION_CATEGORIES);

  for (let i = 0; i < sectionIds.length; i += 1) {
    const sectionId = sectionIds[i];
    const section = sections[sectionId] || {};
    const sectionKey = [pagePath, sectionId].join('::');
    context.sectionCategories[sectionKey] = resolveSectionCategoryList(sectionId, section, context.attributesMode);

    for (let c = 0; c < categories.length; c += 1) {
      const category = categories[c];
      if (!context.firstSections[category] && sectionMatchesCategory(sectionKey, sectionId, category, context)) {
        context.firstSections[category] = [pagePath, sectionId].join('::');
      }
    }
  }
}

function readAttributesMode(metrics) {
  const mode = metrics && metrics.session ? metrics.session.attributes_mode : null;
  return mode === BB_ATTRIBUTES_MODE.REPLACE ? BB_ATTRIBUTES_MODE.REPLACE : BB_ATTRIBUTES_MODE.MERGE;
}

function resolveFieldCategoryList(fieldId, field, attributesMode) {
  const explicit = readCategoryList(field && field.field_categories, Object.keys(FIELD_CATEGORIES));
  if (attributesMode === BB_ATTRIBUTES_MODE.REPLACE) return explicit;

  return uniqueValues(explicit.concat(inferredCategoryList(fieldId, FIELD_CATEGORIES)));
}

function resolveSectionCategoryList(sectionId, section, attributesMode) {
  const explicit = readCategoryList(section && section.section_categories, Object.keys(SECTION_CATEGORIES));
  if (attributesMode === BB_ATTRIBUTES_MODE.REPLACE) return explicit;

  return uniqueValues(explicit.concat(inferredCategoryList(sectionId, SECTION_CATEGORIES)));
}

function readCategoryList(value, allowed) {
  if (!Array.isArray(value)) return [];

  const out = [];
  for (let i = 0; i < value.length; i += 1) {
    if (typeof value[i] !== 'string') continue;
    const category = value[i].trim().toLowerCase();
    if (allowed.indexOf(category) === -1 || out.indexOf(category) !== -1) continue;
    out.push(category);
  }

  return out;
}

function inferredCategoryList(value, categoryMap) {
  const categories = [];
  const names = Object.keys(categoryMap);

  for (let i = 0; i < names.length; i += 1) {
    const category = names[i];
    if (nameMatchesCategory(value, categoryMap[category])) categories.push(category);
  }

  return categories;
}

function isFieldMetric(path, metricPath) {
  const ref = extractFieldRef(path);
  return !!ref && ref.metricPath === metricPath;
}

function isSessionMetric(path, metricPath) {
  return String(path || '') === 'session.' + metricPath;
}

function isFieldMetricInCategory(path, metricPath, categories, scoreContext) {
  const ref = extractFieldRef(path);
  if (!ref || ref.metricPath !== metricPath) return false;

  for (let i = 0; i < categories.length; i += 1) {
    if (fieldRefMatchesCategory(ref, categories[i], scoreContext)) return true;
  }

  return false;
}

function isAmountRangeFieldMetric(path, metricPath, scoreContext) {
  const ref = extractFieldRef(path);
  if (!ref || ref.metricPath !== metricPath) return false;
  return fieldRefMatchesCategory(ref, 'amount', scoreContext) && fieldType(ref, scoreContext) === 'range';
}

function isRoundedNumberMetric(path, scoreContext) {
  const ref = extractFieldRef(path);
  if (!ref || ref.metricPath !== 'value_looks_rounded') return false;

  const type = fieldType(ref, scoreContext);
  return (fieldRefMatchesCategory(ref, 'amount', scoreContext) && type === 'range') || type === 'number';
}

function isFirstSectionMetric(path, category, metricPath, scoreContext) {
  const ref = extractSectionRef(path);
  if (!ref || ref.metricPath !== metricPath) return false;

  const firstSectionKey = scoreContext && scoreContext.firstSections ? scoreContext.firstSections[category] : null;
  if (firstSectionKey) return ref.key === firstSectionKey;
  return sectionMatchesCategory(ref.key, ref.sectionId, category, scoreContext);
}

function fieldRefMatchesCategory(ref, category, scoreContext) {
  const categories = scoreContext && scoreContext.fieldCategories ? scoreContext.fieldCategories[ref.key] : null;
  return !!categories && categories.indexOf(category) !== -1;
}

function sectionMatchesCategory(sectionKey, sectionId, category, scoreContext) {
  const categories = scoreContext && scoreContext.sectionCategories ? scoreContext.sectionCategories[sectionKey] : null;
  return !!categories && categories.indexOf(category) !== -1;
}

function fieldType(ref, scoreContext) {
  if (!scoreContext || !scoreContext.fieldTypes) return null;
  return scoreContext.fieldTypes[ref.key] || null;
}

function extractFieldRef(path) {
  const text = String(path || '');
  const fieldsIndex = text.lastIndexOf('.fields.');
  if (fieldsIndex === -1) return null;

  const beforeFields = text.slice(0, fieldsIndex);
  const formsIndex = beforeFields.lastIndexOf('.forms.');
  if (formsIndex === -1) return null;

  const pagePath = beforeFields.slice(0, formsIndex);
  const formId = beforeFields.slice(formsIndex + '.forms.'.length);
  const fieldStart = fieldsIndex + '.fields.'.length;
  const fieldEnd = text.indexOf('.', fieldStart);
  if (fieldEnd === -1) return null;

  const fieldId = text.slice(fieldStart, fieldEnd);
  const metricPath = text.slice(fieldEnd + 1);

  return {
    pagePath,
    formId,
    fieldId,
    metricPath,
    key: [pagePath, formId, fieldId].join('::'),
  };
}

function extractSectionRef(path) {
  const text = String(path || '');
  const sectionsIndex = text.lastIndexOf('.sections.');
  if (sectionsIndex === -1) return null;

  const pagePath = text.slice(0, sectionsIndex);
  const sectionStart = sectionsIndex + '.sections.'.length;
  const sectionEnd = text.indexOf('.', sectionStart);
  if (sectionEnd === -1) return null;

  const sectionId = text.slice(sectionStart, sectionEnd);
  const metricPath = text.slice(sectionEnd + 1);

  return {
    pagePath,
    sectionId,
    metricPath,
    key: [pagePath, sectionId].join('::'),
  };
}

function detailRow(label, value) {
  return `
    <div class="rs-bb-devtools-detail">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
    </div>
  `;
}

function saveDetails(save) {
  if (save.hold_timeout && save.save_status === 'ok') return 'hold timeout elapsed; save completed later';
  if (save.hold_timeout && save.save_status === 'error')
    return 'hold timeout elapsed; save failed later: ' + save.error;
  if (save.error) return 'save: ' + save.error;
  if (save.processed_status === 'auth_required') return save.processed_error || 'Processed session requires x-api-key';
  if (save.processed_error) return 'processed: ' + save.processed_error;
  return '';
}

function flattenMetrics(value, prefix, rows) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const keys = Object.keys(value);
    if (!keys.length && prefix) {
      rows.push({ path: prefix, value: '{}' });
      return;
    }

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const path = prefix ? prefix + '.' + key : key;
      flattenMetrics(value[key], path, rows);
    }

    return;
  }

  rows.push({ path: prefix || 'metrics', value });
}

function filterRows(rows, scoreContext) {
  const query = searchValue().toLowerCase();
  const selectedScores = activeScoreFilters();
  if (!query && !selectedScores.length) return rows;

  const filtered = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const haystack = row.path + ' ' + formatValue(row.value);
    const matchesQuery = !query || haystack.toLowerCase().indexOf(query) !== -1;
    const matchesScore = !selectedScores.length || pathMatchesSelectedScores(row.path, scoreContext, selectedScores);
    if (matchesQuery && matchesScore) filtered.push(row);
  }

  return filtered;
}

function filterFields(fields, scoreContext) {
  const selectedCategories = activeFieldCategoryFilters();
  const selectedScores = activeScoreFilters();
  if (!selectedCategories.length && !selectedScores.length) return fields;

  const filtered = [];
  for (let i = 0; i < fields.length; i += 1) {
    const item = fields[i];
    const matchesCategory =
      !selectedCategories.length || valuesIntersect(resolvedFieldCategories(item, scoreContext), selectedCategories);
    const matchesScore = !selectedScores.length || fieldMatchesSelectedScores(item, scoreContext, selectedScores);

    if (matchesCategory && matchesScore) filtered.push(item);
  }

  return filtered;
}

function filterSections(sections, scoreContext) {
  const selectedScores = activeScoreFilters();
  if (!selectedScores.length) return sections;

  const filtered = [];
  for (let i = 0; i < sections.length; i += 1) {
    const item = sections[i];
    if (sectionMatchesSelectedScores(item, scoreContext, selectedScores)) filtered.push(item);
  }

  return filtered;
}

function fieldMatchesSelectedScores(item, scoreContext, selectedScores) {
  const rows = [];
  flattenMetrics(item.field || {}, '', rows);

  for (let i = 0; i < rows.length; i += 1) {
    if (pathMatchesSelectedScores(fieldMetricFullPath(item, rows[i].path), scoreContext, selectedScores)) return true;
  }

  return false;
}

function sectionMatchesSelectedScores(item, scoreContext, selectedScores) {
  const rows = [];
  flattenMetrics(item.section || {}, '', rows);

  for (let i = 0; i < rows.length; i += 1) {
    if (pathMatchesSelectedScores(sectionMetricFullPath(item, rows[i].path), scoreContext, selectedScores)) return true;
  }

  return false;
}

function pathMatchesSelectedScores(path, scoreContext, selectedScores) {
  const scores = scoreTagsForPath(path, scoreContext);
  return valuesIntersect(scores, selectedScores);
}

function activeScoreFilters() {
  return activeFilterKeys(scoreFilters, SCORE_KEYS);
}

function activeFieldCategoryFilters() {
  return activeFilterKeys(fieldCategoryFilters, FIELD_FILTER_KEYS);
}

function activeScoreFilterValue() {
  return activeScoreFilters()[0] || '';
}

function activeFieldCategoryFilterValue() {
  return activeFieldCategoryFilters()[0] || '';
}

function activeFilterKeys(filters, keys) {
  const active = [];

  for (let i = 0; i < keys.length; i += 1) {
    if (filters[keys[i]]) active.push(keys[i]);
  }

  return active;
}

function valuesIntersect(left, right) {
  for (let i = 0; i < left.length; i += 1) {
    if (right.indexOf(left[i]) !== -1) return true;
  }

  return false;
}

function groupByCategory(rows) {
  const groups = {};
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const category = categoryForPath(row.path);
    if (!groups[category]) groups[category] = [];
    groups[category].push(row);
  }

  return groups;
}

function categoryForPath(path) {
  if (path.indexOf('session.') === 0) return 'Session';
  if (path.indexOf('.sections.') !== -1) return 'Section';
  if (path.indexOf('.fields.') !== -1 && path.indexOf('.keystroke.') !== -1) return 'Keystroke';
  if (path.indexOf('.fields.') !== -1 && path.indexOf('.corrections.') !== -1) return 'Correction';
  if (path.indexOf('.fields.') !== -1 && path.indexOf('paste') !== -1) return 'Paste';
  if (path.indexOf('pointer') !== -1 || path.indexOf('touch') !== -1) return 'Pointer';
  if (path.indexOf('.fields.') !== -1) return 'Field';
  if (path.indexOf('.forms.') !== -1) return 'Form';
  if (path.indexOf('pages.') === 0) return 'Page';
  return 'Other';
}

function collectFields(metrics) {
  const fields = [];
  const pages = metrics.pages || {};
  const pageIds = Object.keys(pages);

  for (let p = 0; p < pageIds.length; p++) {
    const pageId = pageIds[p];
    const forms = pages[pageId].forms || {};
    const formIds = Object.keys(forms);

    for (let f = 0; f < formIds.length; f++) {
      const formId = formIds[f];
      const formFields = forms[formId].fields || {};
      const fieldIds = Object.keys(formFields);

      for (let i = 0; i < fieldIds.length; i++) {
        const fieldId = fieldIds[i];
        fields.push({
          pageId,
          formId,
          fieldId,
          field: formFields[fieldId],
        });
      }
    }
  }

  return fields.sort(compareFieldItems);
}

function collectSections(metrics) {
  const sections = [];
  const pages = metrics.pages || {};
  const pageIds = Object.keys(pages);

  for (let p = 0; p < pageIds.length; p++) {
    const pageId = pageIds[p];
    const pageSections = pages[pageId].sections || {};
    const sectionIds = Object.keys(pageSections);

    for (let i = 0; i < sectionIds.length; i++) {
      const sectionId = sectionIds[i];
      sections.push({
        pageId,
        sectionId,
        section: pageSections[sectionId],
      });
    }
  }

  return sections.sort(compareSectionItems);
}

function compareFieldItems(a, b) {
  return (
    compareText(fieldDisplayName(a), fieldDisplayName(b)) ||
    compareText(a.formId, b.formId) ||
    compareText(a.pageId, b.pageId)
  );
}

function fieldDisplayName(item) {
  return (item && item.field && item.field.name) || (item && item.fieldId) || '';
}

function compareSectionItems(a, b) {
  return compareText(a.sectionId, b.sectionId) || compareText(a.pageId, b.pageId);
}

function compareText(a, b) {
  const left = String(a || '').toLowerCase();
  const right = String(b || '').toLowerCase();

  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function findScores(value) {
  if (!value || typeof value !== 'object') return null;

  if (hasScoreShape(value)) return value;
  if (value.scores && hasScoreShape(value.scores)) return value.scores;
  if (value.behavioral_biometrics && hasScoreShape(value.behavioral_biometrics.scores)) {
    return value.behavioral_biometrics.scores;
  }
  if (value.data && value.data.scores && hasScoreShape(value.data.scores)) return value.data.scores;
  if (value.data && value.data.behavioral_biometrics && hasScoreShape(value.data.behavioral_biometrics.scores)) {
    return value.data.behavioral_biometrics.scores;
  }

  return null;
}

function findProcessedMetrics(value) {
  if (!value || typeof value !== 'object') return null;

  if (value.behavioral_biometrics && typeof value.behavioral_biometrics === 'object') {
    return value.behavioral_biometrics;
  }

  if (value.data && value.data.behavioral_biometrics && typeof value.data.behavioral_biometrics === 'object') {
    return value.data.behavioral_biometrics;
  }

  if (value.pages && typeof value.pages === 'object') return value;

  return null;
}

function hasScoreShape(value) {
  if (!value || typeof value !== 'object') return false;

  for (let i = 0; i < SCORE_KEYS.length; i++) {
    if (typeof value[SCORE_KEYS[i]] !== 'undefined') return true;
  }

  return false;
}

function formatValue(value) {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatNumber(value, suffix) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';

  const formatted = Math.round(number * 1000) / 1000;
  return suffix ? formatted + ' ' + suffix : formatted;
}

function formatTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString();
}

function shortId(value) {
  if (!value) return '';

  const text = String(value);
  return text.length > 12 ? text.slice(0, 8) + '...' : text;
}

function shortMetricPath(path) {
  const parts = String(path || '').split('.');
  if (parts.length <= 2) return String(path || '');

  return parts.slice(-2).join('.');
}

function searchValue() {
  return metricSearch || '';
}

function cssEscape(value) {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(String(value || ''));
  return String(value || '').replace(/["\\]/g, '\\$&');
}

function closest(target, selector) {
  let el = target;
  while (el && el.nodeType === 1) {
    if (el.matches && el.matches(selector)) return el;
    el = el.parentNode;
  }

  return null;
}

function readOpenState() {
  try {
    return localStorage.getItem(OPEN_STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function writeOpenState(value) {
  try {
    localStorage.setItem(OPEN_STORAGE_KEY, value ? '1' : '0');
  } catch (e) {}
}

function readPanelPosition() {
  try {
    const value = localStorage.getItem(PANEL_POSITION_STORAGE_KEY) || 'right';
    return PANEL_POSITIONS.indexOf(value) === -1 ? 'right' : value;
  } catch (e) {
    return 'right';
  }
}

function writePanelPosition(value) {
  try {
    localStorage.setItem(PANEL_POSITION_STORAGE_KEY, value);
  } catch (e) {}
}

function escapeClass(value) {
  return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '_');
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function styles() {
  return `
    :host {
      all: initial;
      color-scheme: light;
      font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }

    * {
      box-sizing: border-box;
    }

    .rs-bb-devtools-button {
      position: fixed;
      right: 18px;
      bottom: 18px;
      z-index: 2147483647;
      width: 48px;
      height: 48px;
      border: 0;
      border-radius: 50%;
      background: #293241;
      color: #fff;
      font: 700 14px/1 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.24);
      cursor: pointer;
    }

    .rs-bb-devtools-button.status-running {
      background: #207a59;
    }

    .rs-bb-devtools-button.status-paused {
      background: #9a6700;
    }

    .rs-bb-devtools-panel {
      position: fixed;
      right: 18px;
      bottom: 78px;
      z-index: 2147483647;
      display: none;
      flex-direction: column;
      width: min(760px, calc(100vw - 36px));
      height: min(760px, calc(100vh - 108px));
      overflow: hidden;
      border: 1px solid #c8ced8;
      border-radius: 8px;
      background: rgba(246, 248, 251, 0.72);
      color: #17202c;
      backdrop-filter: blur(8px);
      box-shadow: 0 18px 48px rgba(0, 0, 0, 0.26);
      font: 13px/1.4 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }

    .rs-bb-devtools-panel.position-bottom {
      right: 18px;
      bottom: 18px;
      left: 18px;
      width: auto;
      height: min(430px, calc(100vh - 36px));
    }

    .rs-bb-devtools-panel.position-full {
      top: 18px;
      right: 18px;
      bottom: 18px;
      left: 18px;
      width: auto;
      height: auto;
    }

    .rs-bb-devtools-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 16px;
      padding: 12px 14px;
      border-bottom: 1px solid #d7dde6;
      background: rgba(255, 255, 255, 0.62);
    }

    .rs-bb-devtools-title {
      font-size: 15px;
      font-weight: 700;
    }

    .rs-bb-devtools-header-actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 8px;
    }

    .rs-bb-devtools-segmented {
      display: inline-flex;
      overflow: hidden;
      border: 1px solid #c8ced8;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.7);
    }

    .rs-bb-devtools-pill,
    .rs-bb-devtools-tag {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      padding: 3px 8px;
      border-radius: 999px;
      background: #e7ebf1;
      color: #293241;
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
    }

    .rs-bb-devtools-pill.status-running,
    .rs-bb-devtools-pill.save-ok {
      background: #d9f1e7;
      color: #145c42;
    }

    .rs-bb-devtools-pill.save-error,
    .rs-bb-devtools-pill.save-timeout {
      background: #fde2dd;
      color: #9c2b1e;
    }

    .rs-bb-devtools-close,
    .rs-bb-devtools-copy,
    .rs-bb-devtools-icon-button,
    .rs-bb-devtools-inspect,
    .rs-bb-devtools-reset,
    .rs-bb-devtools-segment,
    .rs-bb-devtools-tab {
      border: 1px solid #c8ced8;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.7);
      color: #17202c;
      font: 600 12px/1 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      cursor: pointer;
    }

    .rs-bb-devtools-close,
    .rs-bb-devtools-copy,
    .rs-bb-devtools-inspect,
    .rs-bb-devtools-reset {
      padding: 7px 10px;
    }

    .rs-bb-devtools-reset {
      border-color: #d7a9a0;
      background: #fff5f2;
      color: #8c2f22;
    }

    .rs-bb-devtools-inspect {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 30px;
      padding: 0;
    }

    .rs-bb-devtools-inspect.active {
      border-color: #2f5f98;
      background: #dfeafb;
      color: #173f6f;
    }

    .rs-bb-devtools-icon {
      display: block;
      width: 16px;
      height: 16px;
      fill: none;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .rs-bb-devtools-segment {
      border: 0;
      border-radius: 0;
      padding: 7px 9px;
      background: transparent;
      text-transform: capitalize;
    }

    .rs-bb-devtools-segment.active {
      background: #dfeafb;
      color: #173f6f;
    }

    .rs-bb-devtools-icon-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      width: 30px;
      height: 28px;
      padding: 0;
      background: rgba(247, 249, 252, 0.78);
    }

    .rs-bb-devtools-chevron {
      width: 8px;
      height: 8px;
      border-right: 2px solid #2f5f98;
      border-bottom: 2px solid #2f5f98;
      transform: translateY(-2px) rotate(45deg);
    }

    .rs-bb-devtools-icon-button.collapsed .rs-bb-devtools-chevron {
      transform: translateX(-2px) rotate(-45deg);
    }

    .rs-bb-devtools-main {
      display: flex;
      flex-direction: column;
      min-height: 0;
      width: 100%;
    }

    .rs-bb-devtools-tabs {
      display: flex;
      flex: 0 0 auto;
      gap: 6px;
      overflow-x: auto;
      padding: 8px 10px;
      border-bottom: 1px solid #d7dde6;
      background: rgba(237, 241, 246, 0.62);
      scrollbar-width: thin;
    }

    .rs-bb-devtools-tab {
      flex: 0 0 auto;
      padding: 7px 9px;
      white-space: nowrap;
    }

    .rs-bb-devtools-tab.active {
      border-color: #2f5f98;
      background: #dfeafb;
      color: #173f6f;
    }

    .rs-bb-devtools-body {
      flex: 1 1 auto;
      min-width: 0;
      overflow: auto;
      padding: 14px;
    }

    .rs-bb-devtools-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
      gap: 10px;
      margin-bottom: 12px;
    }

    .rs-bb-devtools-stat,
    .rs-bb-devtools-card {
      border: 1px solid #d7dde6;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.74);
    }

    .rs-bb-devtools-stat {
      padding: 12px;
    }

    .rs-bb-devtools-stat-label {
      color: #687386;
      font-size: 12px;
    }

    .rs-bb-devtools-stat-value {
      margin-top: 4px;
      font-size: 20px;
      font-weight: 700;
    }

    .rs-bb-devtools-card {
      margin-bottom: 12px;
      padding: 12px;
    }

    .rs-bb-devtools-card h3 {
      margin: 0 0 10px;
      font-size: 13px;
    }

    .rs-bb-devtools-card-heading {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 10px;
    }

    .rs-bb-devtools-card-heading h3 {
      margin: 0 0 3px;
    }

    .rs-bb-devtools-card-heading span {
      color: #687386;
      font-size: 12px;
      font-weight: 600;
    }

    .rs-bb-devtools-card-collapsed .rs-bb-devtools-card-heading {
      margin-bottom: 0;
    }

    .rs-bb-devtools-toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
    }

    .rs-bb-devtools-filter-group {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }

    .rs-bb-devtools-filter-label {
      color: #586476;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .rs-bb-devtools-filter-select {
      min-width: 132px;
      min-height: 34px;
      border: 1px solid #c8ced8;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.82);
      color: #17202c;
      padding: 7px 28px 7px 9px;
      font: 600 12px/1.2 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      text-transform: capitalize;
      cursor: pointer;
    }

    .rs-bb-devtools-search {
      flex: 1 1 220px;
      min-width: 0;
      min-height: 34px;
      border: 1px solid #c8ced8;
      border-radius: 6px;
      padding: 7px 10px;
      font: 13px/1.3 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }

    .rs-bb-devtools-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      background: #fff;
    }

    .rs-bb-devtools-table th,
    .rs-bb-devtools-table td {
      border-bottom: 1px solid #e3e7ee;
      padding: 8px;
      text-align: left;
      vertical-align: top;
      font-size: 12px;
      overflow-wrap: anywhere;
    }

    .rs-bb-devtools-table th {
      color: #586476;
      font-weight: 700;
    }

    .rs-bb-devtools-table.compact td {
      padding: 5px 6px;
    }

    .rs-bb-devtools-path {
      width: 58%;
      color: #293241;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    }

    .rs-bb-devtools-value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      color: #135f45;
      word-break: break-word;
    }

    .rs-bb-devtools-value.collapsed {
      display: flex;
      gap: 10px;
      align-items: baseline;
      justify-content: space-between;
    }

    .rs-bb-devtools-metric-key {
      flex: 0 1 52%;
      color: #586476;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rs-bb-devtools-score-tags {
      display: inline-flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-left: 6px;
      vertical-align: middle;
    }

    .rs-bb-devtools-score-tag {
      display: inline-flex;
      align-items: center;
      min-height: 18px;
      padding: 2px 5px;
      border-radius: 999px;
      background: #edf4ff;
      color: #24558d;
      font: 700 10px/1 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }

    .rs-bb-devtools-field-list,
    .rs-bb-devtools-section-list {
      display: grid;
      gap: 12px;
    }

    .rs-bb-devtools-field-heading,
    .rs-bb-devtools-section-heading {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 10px;
    }

    .rs-bb-devtools-field-card.selected,
    .rs-bb-devtools-section-card.selected {
      border-color: #2f6fbc;
      box-shadow: 0 0 0 3px rgba(47, 111, 188, 0.16);
    }

    .rs-bb-devtools-field-heading h3,
    .rs-bb-devtools-section-heading h3 {
      margin-bottom: 4px;
    }

    .rs-bb-devtools-field-subtitle,
    .rs-bb-devtools-section-subtitle {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      color: #586476;
      font-size: 12px;
      font-weight: 600;
    }

    .rs-bb-devtools-field-categories {
      display: inline-flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .rs-bb-devtools-field-category {
      display: inline-flex;
      align-items: center;
      min-height: 18px;
      padding: 2px 6px;
      border-radius: 999px;
      background: #eef1f5;
      color: #4d596b;
      font: 700 10px/1 Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      text-transform: uppercase;
    }

    .rs-bb-devtools-field-category.category-personal {
      background: #edf4ff;
      color: #24558d;
    }

    .rs-bb-devtools-field-category.category-sensitive {
      background: #fdebea;
      color: #9c2b1e;
    }

    .rs-bb-devtools-field-category.category-amount {
      background: #e8f5ee;
      color: #196447;
    }

    .rs-bb-devtools-field-category.category-unrecognized {
      background: #eef1f5;
      color: #586476;
    }

    .rs-bb-devtools-field-summary,
    .rs-bb-devtools-section-summary {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(112px, 1fr));
      gap: 8px;
      margin-bottom: 10px;
    }

    .rs-bb-devtools-field-summary .rs-bb-devtools-stat,
    .rs-bb-devtools-section-summary .rs-bb-devtools-stat {
      padding: 9px;
    }

    .rs-bb-devtools-field-summary .rs-bb-devtools-stat-value,
    .rs-bb-devtools-section-summary .rs-bb-devtools-stat-value {
      font-size: 16px;
    }

    .rs-bb-devtools-details {
      border-top: 1px solid #e3e7ee;
      padding-top: 8px;
    }

    .rs-bb-devtools-details summary {
      color: #2f5f98;
      cursor: pointer;
      font-weight: 700;
      margin-bottom: 8px;
    }

    .rs-bb-devtools-summary {
      appearance: none;
      background: transparent;
      border: 0;
      color: #2f5f98;
      cursor: pointer;
      font: inherit;
      font-weight: 700;
      margin: 0 0 8px;
      padding: 0;
      text-align: left;
    }

    .rs-bb-devtools-empty {
      padding: 18px;
      border: 1px dashed #c8ced8;
      border-radius: 8px;
      color: #687386;
      background: rgba(255, 255, 255, 0.64);
    }

    .rs-bb-devtools-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .rs-bb-devtools-score {
      display: grid;
      grid-template-columns: 180px 1fr 56px;
      align-items: center;
      gap: 10px;
      margin: 10px 0;
    }

    .rs-bb-devtools-score-panel {
      padding: 8px 0 12px;
      border-bottom: 1px solid #e3e7ee;
    }

    .rs-bb-devtools-score-panel:last-child {
      border-bottom: 0;
      padding-bottom: 0;
    }

    .rs-bb-devtools-score-panel-heading {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 30px;
      align-items: center;
      gap: 10px;
    }

    .rs-bb-devtools-score-panel-heading .rs-bb-devtools-score {
      margin: 6px 0;
    }

    .rs-bb-devtools-contributors {
      margin-top: 6px;
    }

    .rs-bb-devtools-contributors td:first-child {
      width: 150px;
      color: #586476;
      font-weight: 700;
    }

    .rs-bb-devtools-score-label {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    }

    .rs-bb-devtools-score-track {
      height: 8px;
      overflow: hidden;
      border-radius: 999px;
      background: #e2e7ee;
    }

    .rs-bb-devtools-score-track div {
      height: 100%;
      border-radius: 999px;
      background: #2f6fbc;
    }

    .rs-bb-devtools-score-value {
      text-align: right;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    }

    .rs-bb-devtools-detail {
      display: grid;
      grid-template-columns: 180px 1fr;
      gap: 10px;
      padding: 8px 0;
      border-bottom: 1px solid #e3e7ee;
    }

    .rs-bb-devtools-detail span {
      color: #687386;
    }

    .rs-bb-devtools-detail strong {
      overflow-wrap: anywhere;
      font-weight: 600;
    }

    .rs-bb-devtools-muted {
      color: #687386;
      font-size: 12px;
    }

    .rs-bb-devtools-save-list {
      display: grid;
      gap: 12px;
    }

    .rs-bb-devtools-save-heading {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 12px;
    }

    .rs-bb-devtools-save-heading h3 {
      margin-bottom: 4px;
    }

    .rs-bb-devtools-save-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      color: #586476;
      font-size: 12px;
      font-weight: 600;
    }

    .rs-bb-devtools-timeline {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 8px;
      margin-bottom: 10px;
    }

    .rs-bb-devtools-timeline-step {
      display: grid;
      grid-template-columns: 12px 1fr;
      gap: 8px;
      min-width: 0;
      padding: 9px;
      border: 1px solid #d7dde6;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.74);
    }

    .rs-bb-devtools-timeline-dot {
      width: 10px;
      height: 10px;
      margin-top: 3px;
      border-radius: 50%;
      background: #9aa5b5;
    }

    .rs-bb-devtools-timeline-step.state-ready .rs-bb-devtools-timeline-dot {
      background: #207a59;
    }

    .rs-bb-devtools-timeline-step.state-loading .rs-bb-devtools-timeline-dot {
      background: #2f6fbc;
    }

    .rs-bb-devtools-timeline-step.state-warning .rs-bb-devtools-timeline-dot {
      background: #9a6700;
    }

    .rs-bb-devtools-timeline-step.state-error .rs-bb-devtools-timeline-dot {
      background: #c2412d;
    }

    .rs-bb-devtools-timeline-label {
      overflow-wrap: anywhere;
      font-weight: 800;
    }

    .rs-bb-devtools-timeline-detail {
      margin-top: 3px;
      color: #586476;
      font-size: 12px;
      overflow-wrap: anywhere;
    }

    .rs-bb-devtools-save-error {
      margin: 8px 0 10px;
      padding: 8px;
      border: 1px solid #f1b7ae;
      border-radius: 6px;
      background: #fde2dd;
      color: #8a2619;
      font-size: 12px;
      font-weight: 700;
    }

    pre {
      max-height: 420px;
      margin: 0;
      overflow: auto;
      border: 1px solid #e3e7ee;
      border-radius: 6px;
      background: #101820;
      color: #d7e3ef;
      padding: 12px;
      font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    @media (max-width: 760px) {
      .rs-bb-devtools-button {
        right: calc(12px + env(safe-area-inset-right, 0px));
        bottom: calc(12px + env(safe-area-inset-bottom, 0px));
        width: 44px;
        height: 44px;
      }

      .rs-bb-devtools-panel {
        top: env(safe-area-inset-top, 0px);
        right: env(safe-area-inset-right, 0px);
        bottom: env(safe-area-inset-bottom, 0px);
        left: env(safe-area-inset-left, 0px);
        width: auto;
        height: auto;
        max-height: none;
        border-radius: 0;
      }

      .rs-bb-devtools-panel.position-bottom,
      .rs-bb-devtools-panel.position-full {
        top: env(safe-area-inset-top, 0px);
        right: env(safe-area-inset-right, 0px);
        bottom: env(safe-area-inset-bottom, 0px);
        left: env(safe-area-inset-left, 0px);
        width: auto;
        height: auto;
      }

      .rs-bb-devtools-header {
        align-items: stretch;
        gap: 8px;
        padding: 10px;
      }

      .rs-bb-devtools-title {
        flex: 1 1 auto;
        min-width: 160px;
        font-size: 14px;
      }

      .rs-bb-devtools-header-actions {
        width: 100%;
        justify-content: flex-start;
        gap: 6px;
      }

      .rs-bb-devtools-segmented {
        flex: 0 1 auto;
      }

      .rs-bb-devtools-segment {
        min-width: 0;
        min-height: 34px;
        padding: 8px 6px;
      }

      .rs-bb-devtools-icon-button {
        width: 34px;
        height: 34px;
      }

      .rs-bb-devtools-close,
      .rs-bb-devtools-copy,
      .rs-bb-devtools-reset {
        min-height: 34px;
        padding: 8px 9px;
      }

      .rs-bb-devtools-tabs {
        border-bottom: 1px solid #d7dde6;
        padding: 7px 8px;
        gap: 5px;
      }

      .rs-bb-devtools-tab {
        padding: 8px 9px;
      }

      .rs-bb-devtools-body {
        padding: 10px;
      }

      .rs-bb-devtools-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
      }

      .rs-bb-devtools-card,
      .rs-bb-devtools-stat {
        border-radius: 6px;
      }

      .rs-bb-devtools-card {
        padding: 10px;
      }

      .rs-bb-devtools-stat {
        padding: 10px;
      }

      .rs-bb-devtools-stat-value {
        font-size: 17px;
      }

      .rs-bb-devtools-toolbar {
        gap: 6px;
      }

      .rs-bb-devtools-search,
      .rs-bb-devtools-filter-group,
      .rs-bb-devtools-filter-select {
        flex: 1 1 100%;
        width: 100%;
      }

      .rs-bb-devtools-filter-group {
        align-items: stretch;
        flex-direction: column;
        gap: 4px;
      }

      .rs-bb-devtools-card-heading,
      .rs-bb-devtools-field-heading,
      .rs-bb-devtools-section-heading,
      .rs-bb-devtools-save-heading {
        gap: 8px;
      }

      .rs-bb-devtools-field-summary,
      .rs-bb-devtools-section-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .rs-bb-devtools-timeline {
        grid-template-columns: 1fr;
      }

      .rs-bb-devtools-score {
        grid-template-columns: 1fr 56px;
        gap: 6px;
      }

      .rs-bb-devtools-score-label {
        grid-column: 1 / -1;
      }

      .rs-bb-devtools-score-panel-heading {
        grid-template-columns: minmax(0, 1fr) 30px;
      }

      .rs-bb-devtools-contributors td:first-child,
      .rs-bb-devtools-path {
        width: 44%;
      }

      .rs-bb-devtools-detail {
        grid-template-columns: 1fr;
        gap: 3px;
      }

      pre {
        max-height: 52vh;
        padding: 10px;
        font-size: 11px;
      }
    }

    @media (max-width: 420px) {
      .rs-bb-devtools-grid,
      .rs-bb-devtools-field-summary,
      .rs-bb-devtools-section-summary {
        grid-template-columns: 1fr;
      }

      .rs-bb-devtools-header-actions {
        align-items: stretch;
      }

      .rs-bb-devtools-inspect,
      .rs-bb-devtools-close,
      .rs-bb-devtools-reset {
        flex: 0 0 auto;
      }

      .rs-bb-devtools-card-heading,
      .rs-bb-devtools-field-heading,
      .rs-bb-devtools-section-heading,
      .rs-bb-devtools-save-heading {
        flex-direction: column;
      }

      .rs-bb-devtools-card-heading .rs-bb-devtools-copy {
        align-self: flex-start;
      }
    }
  `;
}
